<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { adapter, type SearchResultGroup } from '@/services/prototype-adapter'
import { PAGE_DEFINITIONS } from '@/data/registry'
import { useAuthStore } from '@/stores/auth'
import { useSessionCacheStore } from '@/stores/session-cache'
import AppIcon from '@/components/AppIcon.vue'
import { request } from '@/services/request'

const props = defineProps<{ open: boolean }>()
const emit = defineEmits<{ close: [] }>()

const router = useRouter()
const auth = useAuthStore()
const cache = useSessionCacheStore()
const input = ref<HTMLInputElement | null>(null)
const term = ref('')
const activeIndex = ref(0)
let opener: HTMLElement | null = null

interface CommandItem {
  id: string
  title: string
  sub: string
  route: string
  group: string
  icon: string
}

const pageItems = computed<CommandItem[]>(() => PAGE_DEFINITIONS
  .filter(page => page.kind !== 'state' && page.permission !== 'public' && auth.has(page.permission))
  .map(page => ({
    id: `page-${page.id}`,
    title: page.title,
    sub: page.purpose,
    route: page.route,
    group: '页面导航',
    icon: page.icon,
  })))

const dataGroups = ref<SearchResultGroup[]>([])
const searching = ref(false)
let debounceTimer = 0
let controller: AbortController | null = null

async function runDataSearch(): Promise<void> {
  const text = term.value.trim()
  controller?.abort()
  if (!text) {
    dataGroups.value = []
    searching.value = false
    return
  }
  controller = new AbortController()
  searching.value = true
  try {
    dataGroups.value = await request(() => adapter.search(text), { delay: 80, signal: controller.signal })
  } catch {
    if (!controller.signal.aborted) dataGroups.value = []
  } finally {
    if (!controller.signal.aborted) searching.value = false
  }
}

const SEARCH_PERMISSION: Record<string, string> = {
  用户: 'users.view',
  家庭: 'families.view',
  药品: 'drugs.view',
  会员订单: 'membership.view',
  客服工单: 'support.view',
}

const dataItems = computed<CommandItem[]>(() => dataGroups.value
  .filter(group => {
    const permission = SEARCH_PERMISSION[group.label]
    return !permission || auth.has(permission)
  })
  .flatMap(group => group.items.map(item => ({
    id: `${group.label}-${item.id}`,
    title: item.title,
    sub: item.sub,
    route: item.route,
    group: group.label,
    icon: 'records',
  }))))

const filteredPages = computed<CommandItem[]>(() => {
  const text = term.value.trim().toLowerCase()
  if (!text) return pageItems.value.slice(0, 6)
  return pageItems.value.filter(item => `${item.title} ${item.sub}`.toLowerCase().includes(text)).slice(0, 6)
})

const items = computed<CommandItem[]>(() => [...dataItems.value.slice(0, 12), ...filteredPages.value])

const groups = computed(() => {
  const map = new Map<string, CommandItem[]>()
  for (const item of items.value) {
    const list = map.get(item.group) ?? []
    list.push(item)
    map.set(item.group, list)
  }
  return Array.from(map.entries()).map(([label, list]) => ({ label, items: list }))
})

watch(() => props.open, async open => {
  if (!open) {
    opener?.focus()
    opener = null
    return
  }
  opener = document.activeElement instanceof HTMLElement ? document.activeElement : null
  term.value = ''
  dataGroups.value = []
  activeIndex.value = 0
  await nextTick()
  input.value?.focus()
})

watch(term, () => {
  activeIndex.value = 0
  window.clearTimeout(debounceTimer)
  debounceTimer = window.setTimeout(() => { void runDataSearch() }, 300)
})

onBeforeUnmount(() => {
  window.clearTimeout(debounceTimer)
  controller?.abort()
})

function choose(item: CommandItem | undefined): void {
  if (!item) return
  cache.addRecent(item.title)
  emit('close')
  void router.push(item.route.replace(/^#/, ''))
}

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('close')
    return
  }
  if (event.key === 'ArrowDown') {
    event.preventDefault()
    activeIndex.value = Math.min(items.value.length - 1, activeIndex.value + 1)
    scrollActive()
    return
  }
  if (event.key === 'ArrowUp') {
    event.preventDefault()
    activeIndex.value = Math.max(0, activeIndex.value - 1)
    scrollActive()
    return
  }
  if (event.key === 'Enter') {
    event.preventDefault()
    choose(items.value[activeIndex.value])
  }
}

function scrollActive(): void {
  nextTick(() => {
    document.querySelector('.command-item.is-active')?.scrollIntoView({ block: 'nearest' })
  })
}

function indexOf(item: CommandItem): number {
  return items.value.findIndex(entry => entry.id === item.id)
}
</script>

<template>
  <Teleport to="body">
    <Transition name="command">
      <div v-if="open" class="command-overlay" @mousedown.self="emit('close')">
        <section class="command-panel" role="dialog" aria-modal="true" aria-label="全局搜索">
          <div class="command-input-row">
            <AppIcon name="search" :size="18" />
            <input
              ref="input"
              v-model="term"
              class="command-input"
              type="search"
              placeholder="搜索页面、用户、家庭、药品、订单或工单"
              aria-label="搜索页面与业务数据"
              @keydown="onKeydown"
            />
            <span class="kbd">Esc</span>
          </div>
          <div class="command-body">
            <div v-if="!term && cache.recentSearches.length" class="command-recent">
              <span class="command-group__label">最近搜索</span>
              <button v-for="item in cache.recentSearches" :key="item" type="button" class="recent-chip" @click="term = item">{{ item }}</button>
            </div>
            <template v-for="group in groups" :key="group.label">
              <div class="command-group__label">{{ group.label }}</div>
              <button
                v-for="item in group.items"
                :key="item.id"
                type="button"
                class="command-item"
                :class="{ 'is-active': indexOf(item) === activeIndex }"
                @mouseenter="activeIndex = indexOf(item)"
                @click="choose(item)"
              >
                <span class="command-item__icon"><AppIcon :name="item.icon" :size="15" /></span>
                <span class="command-item__text">
                  <span class="command-item__title">{{ item.title }}</span>
                  <span class="command-item__sub">{{ item.sub || item.route }}</span>
                </span>
                <span class="command-item__route">{{ item.route.replace(/^#/, '') }}</span>
              </button>
            </template>
            <div v-if="term && !items.length" class="command-empty">
              <AppIcon name="compass" :size="28" />
              <p>没有找到“{{ term }}”相关内容</p>
              <span>可尝试用户姓名、手机号、订单号或页面名称</span>
            </div>
          </div>
          <footer class="command-foot">
            <span><span class="kbd">↑</span><span class="kbd">↓</span> 选择</span>
            <span><span class="kbd">Enter</span> 打开</span>
            <span><span class="kbd">Esc</span> 关闭</span>
            <span class="command-foot__hint">搜索范围：当前角色可见页面与内存业务数据</span>
          </footer>
        </section>
      </div>
    </Transition>
  </Teleport>
</template>
